import { useEffect } from "react";
import { db, doc, collection, onSnapshot, query, where } from "../lib/firebase";
import { usePartyStore } from "../stores/partyStore";
import type { QueueSong } from "../types";

export function useQueueSync(partyId: string | null) {
  useEffect(() => {
    if (!partyId) {
      const state = usePartyStore.getState();
      if (state.queue.length > 0 || state.currentSong !== null) {
        state.setQueue([]);
        state.setCurrentSong(null);
      }
      return;
    }

    const orderDocRef = doc(db, "parties", partyId, "queueOrder", "main");

    const unsubscribe = onSnapshot(
      orderDocRef,
      async (orderSnap) => {
        const { setQueue, setCurrentSong } = usePartyStore.getState();

        if (!orderSnap.exists()) {
          setQueue([]);
          setCurrentSong(null);
          return;
        }

        const videoIds: string[] = orderSnap.data()?.videoIds || [];
        if (videoIds.length === 0) {
          setQueue([]);
          setCurrentSong(null);
          return;
        }

        try {
          const chunks: string[][] = [];
          for (let i = 0; i < videoIds.length; i += 30) {
            chunks.push(videoIds.slice(i, i + 30));
          }

          const queueMap = new Map<string, QueueSong>();

          for (const chunk of chunks) {
            const q = query(
              collection(db, "parties", partyId, "queue"),
              where("__name__", "in", chunk)
            );

            const snap = await new Promise<any>((resolve, reject) => {
              const unsub = onSnapshot(q, (snapshot) => {
                unsub();
                resolve(snapshot);
              }, reject);
            });

            snap.forEach((docSnap: any) => {
              const data = docSnap.data();
              queueMap.set(docSnap.id, {
                id: docSnap.id,
                videoId: data.id?.videoId || docSnap.id,
                title: data.snippet?.title || data.title || "Unknown Song",
                channelTitle: data.snippet?.channelTitle || data.channelTitle || "Karaoke",
                thumbnailUrl: data.snippet?.thumbnails?.medium?.url || data.snippet?.thumbnails?.default?.url || data.thumbnailUrl || "",
                embeddable: data.status?.embeddable ?? true,
                order: videoIds.indexOf(docSnap.id),
                addedByUserId: data.addedByUserId || "",
                addedByUserName: data.addedByUserName || "Singer",
                addedByUserPhoto: data.addedByUserPhoto || null,
                status: data.status || "queued",
                timestamp: data.timestamp || Date.now(),
              });
            });
          }

          const orderedQueue = videoIds
            .map((id) => queueMap.get(id))
            .filter((item): item is QueueSong => item !== undefined);

          setQueue(orderedQueue);
          setCurrentSong(orderedQueue.length > 0 ? orderedQueue[0] : null);
        } catch (error: any) {
          if (error.code !== "permission-denied") {
            console.error("Error fetching queue items:", error);
          }
        }
      },
      (error) => {
        if (error.code !== "permission-denied") {
          console.error("Queue listener error:", error);
        }
      }
    );

    return () => unsubscribe();
  }, [partyId]);
}
